import { type FC, type ReactNode } from "react"
import { BiChevronDown, BiChevronUp } from "react-icons/bi"

import type { BoxProps, StackProps } from "@chakra-ui/react"
import {
  Box,
  Collapse,
  Flex,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"

import { toPascalCase } from "@supliful/domain"

type HeadingProps = StackProps & {
  heading: string | ReactNode
}

const Heading = ({ heading, ...rest }: HeadingProps) => {
  return (
    <Stack w="full" direction="row" align="center" {...rest}>
      {typeof heading === "string" ? (
        <Text
          fontSize={{ base: 16, sm: 18 }}
          lineHeight={1.2}
          fontWeight={600}
          style={{ userSelect: "none" }}
        >
          {heading}
        </Text>
      ) : (
        heading
      )}
    </Stack>
  )
}

type ContainerProps = {
  heading?: ReactNode
  expandable?: boolean
  children?: ReactNode | ((isOpen: boolean) => ReactNode)
} & Omit<BoxProps, "children"> &
  Partial<ExpandableProps>

type ExpandableProps = {
  heading: ReactNode
  children: ReactNode | ((isOpen: boolean) => ReactNode)
  testId?: string
  expanded?: boolean
}

const Expandable: FC<ExpandableProps> = ({
  children,
  heading,
  expanded,
  ...rest
}) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: expanded })
  const renderedChildren =
    typeof children === "function" ? children(isOpen) : children

  return (
    <Container
      w="100%"
      heading={
        <Flex
          w="full"
          align="center"
          justify="space-between"
          style={{ cursor: "pointer" }}
          onClick={onToggle}
        >
          {heading && <Heading heading={heading} />}
          <Box boxSize="2.5rem" as={isOpen ? BiChevronUp : BiChevronDown} />
        </Flex>
      }
      {...rest}
    >
      <Collapse in={isOpen} animateOpacity style={{ width: "100%" }}>
        <Box
          w="full"
          pt={{ base: 1, sm: 2, md: 4 }}
          overflow="hidden"
          overflowX="auto"
        >
          {renderedChildren}
        </Box>
      </Collapse>
    </Container>
  )
}

export const Container: FC<ContainerProps> = ({
  heading,
  expandable,
  children,
  testId,
  expanded,
  ...props
}) => {
  const _testId =
    testId ??
    (typeof heading === "string"
      ? `container${toPascalCase(heading)}`
      : undefined)

  return expandable ? (
    <Expandable
      testId={_testId}
      heading={heading}
      expanded={expanded}
      {...props}
    >
      {children}
    </Expandable>
  ) : (
    <Box
      px={{ base: 4, sm: 8 }}
      py={{ base: 3, sm: 4 }}
      w="full"
      maxW="full"
      bg="neutrals.50"
      {...(testId && {
        "data-testid": testId,
      })}
      {...props}
    >
      {heading && <Heading heading={heading} minH="2.5rem" />}
      {children}
    </Box>
  )
}
